/**
 * Dispatcher — subscriber registry + viewport-aware notify loop for the
 * editor Flux store.
 *
 * Phase FB.8 of docs/design/frontend-mv-architecture-260524.md §P.3 + §P.4.
 *
 * Responsibilities:
 *   1. `dispatch(op)` — apply the op via the pure reducer (`ir.ts:apply`),
 *      commit `state.ir`, rebuild `state.blocksByPage` if any non-doc-level
 *      page was touched, then route per-page change notifications to
 *      subscribers whose `viewportRange` intersects `dirtyPages`.
 *   2. `subscribe(sub)` / `unsubscribe(sub)` — register/deregister Flux-style
 *      subscribers. Returns an unsubscribe thunk for convenience.
 *   3. `rebuildBlocksByPage(ir)` — exported as a one-shot bootstrap (called
 *      on initial IR load by editor-load-flow.ts so the index is non-empty
 *      before the first dispatch fires).
 *
 * Subscribers carry:
 *   - `viewportRange(): { first: number; last: number }` — re-evaluated per
 *     dispatch. The dispatcher intersects this with the reducer's
 *     `dirtyPages` and only calls `onChange` if (a) the intersection is
 *     non-empty OR (b) `dirtyPages` contains the doc-level sentinel `0`
 *     AND the subscriber declares `wantsDocLevel: true`.
 *   - `onChange(state, visiblePages)` — invoked with the (filtered) set of
 *     dirty pages the subscriber cares about.
 *   - `wantsDocLevel?: boolean` — opt-in to doc-level (page 0) notifications
 *     for statusbar / inspector / pane-title subscribers. Default false.
 *
 * Why this is the load-bearing seam (per §P.3): without per-page routing,
 * a `set-alt-text` op on page 250 would notify the page-1 overlay
 * subscriber (and the 2..N subscribers too), even though no on-screen
 * pixel changes. `dirtyPages` makes the bound
 * `O(notified-subscribers ≤ viewport-window)` regardless of N.
 *
 * Layer: store / actions seam — owns IR mutation commit + blocksByPage
 * index. Per the layer-imports lint, this file is under store.
 *
 * @module dispatcher
 */
import { apply } from "./ir.js";
import { state, _bboxCache } from "./editor-state.js";
// ─── Module-scope registry ────────────────────────────────────────────────────
const _subscribers = new Set();
/**
 * Register a subscriber. Returns an unsubscribe thunk. Idempotent: registering
 * the same subscriber twice is a no-op (Set semantics).
 */
export function subscribe(sub) {
    _subscribers.add(sub);
    return () => { _subscribers.delete(sub); };
}
/** Explicit unsubscribe — equivalent to calling the thunk returned by `subscribe`. */
export function unsubscribe(sub) {
    _subscribers.delete(sub);
}
/**
 * Test-only helper — count of active subscribers. Used by seam tests
 * (`dispatcher.test.mjs`) to assert subscribe/unsubscribe lifecycle.
 */
export function _subscriberCount() {
    return _subscribers.size;
}
/**
 * Test-only helper — clear the subscriber registry. Used by seam tests to
 * isolate per-test state when the dispatcher is module-singleton in a
 * shared test process.
 */
export function _clearSubscribersForTesting() {
    _subscribers.clear();
}
// ─── blocksByPage rebuild ────────────────────────────────────────────────────
/**
 * Compute and assign `state.blocksByPage` from the given IR document. Full
 * O(N-blocks) walk; the per-page index is replaced wholesale.
 *
 * Phase FB.8 simple-path implementation — every non-doc-level dispatch
 * triggers a full rebuild. Profiling in FB.12 will tell us whether this
 * exceeds the 100 ms budget on 25k+ block docs; FB.13 ships a per-page
 * incremental rebuild only if needed.
 *
 * NOTE: composite blocks (List items, Table rows/cells) are NOT walked
 * recursively — `state.blocksByPage` is a top-level-block index. List
 * items + Table cells are dispatched by their containing Block's page.
 */
export function rebuildBlocksByPage(ir) {
    state.blocksByPage.clear();
    if (!ir || !Array.isArray(ir.blocks))
        return;
    for (const b of ir.blocks) {
        let bucket = state.blocksByPage.get(b.page);
        if (bucket === undefined) {
            bucket = [];
            state.blocksByPage.set(b.page, bucket);
        }
        bucket.push(b);
    }
}
const _perf = (() => {
    const g = globalThis;
    if (g.performance && typeof g.performance.mark === "function"
        && typeof g.performance.measure === "function"
        && typeof g.performance.now === "function") {
        return g.performance;
    }
    return null;
})();
/** Sequence id so concurrent dispatches don't collide on mark names. */
let _perfSeq = 0;
function _perfBegin(label) {
    if (_perf === null)
        return null;
    const id = `${label}-${++_perfSeq}`;
    try {
        _perf.mark(`${id}-start`);
    }
    catch (_) { /* swallow — perf is best-effort observability */ }
    return id;
}
function _perfEnd(label, id) {
    if (_perf === null || id === null)
        return;
    try {
        _perf.mark(`${id}-end`);
        _perf.measure(label, `${id}-start`, `${id}-end`);
    }
    catch (_) { /* swallow — perf is best-effort observability */ }
}
// ─── Routing helpers ──────────────────────────────────────────────────────────
/**
 * Intersect a `dirtyPages` Set with a ViewportRange. Page 0 is excluded
 * from the per-page intersection — doc-level routing is handled separately
 * (subscribers opt in via `wantsDocLevel`).
 */
function _intersectWithViewport(dirty, vr) {
    const out = new Set();
    if (vr.first > vr.last)
        return out;
    for (const p of dirty) {
        if (p === 0)
            continue; // doc-level sentinel — handled at the `wantsDocLevel` branch
        if (p >= vr.first && p <= vr.last)
            out.add(p);
    }
    return out;
}
/**
 * Should we rebuild `state.blocksByPage`?
 *
 * Hot-path optimization (§P.4): if `dirtyPages` is empty OR is exactly
 * `{0}` (doc-level only), the block spine is unchanged → skip rebuild.
 */
function _shouldRebuildBlocksByPage(dirty) {
    if (dirty.size === 0)
        return false;
    if (dirty.size === 1 && dirty.has(0))
        return false;
    return true;
}
// ─── Bbox-cache narrow invalidation (Phase FB.11) ────────────────────────────
//
// Geometry-shaped ops mutate the per-page bbox payload that
// `_bboxCache.getForPage(p)` returns; metadata-shaped ops (alt-text, role,
// decorative, etc.) leave geometry untouched. Routing dirtyPages to
// `_bboxCache.invalidatePages` ONLY on the geometry shape preserves the
// metadata-edit cache hit and closes the §P.4 "monotonically grows" gap.
//
// `applyZoom`'s full clear remains the right call because zoom rescales
// every cached coordinate — every entry is stale.
//
// See `docs/design/frontend-mv-architecture-260524.md` §P.7.
/**
 * Op-kinds whose `dirtyPages` reflect geometry changes. A geometry op on
 * page P invalidates page P's bbox-cache entry; pages outside `dirtyPages`
 * stay cached.
 */
const GEOMETRY_OP_KINDS = new Set([
    "set-bbox",
    "create-element",
    "delete-element",
    "reorder-reading",
]);
/**
 * Predicate: should this op trigger bbox-cache invalidation? Recursive on
 * BATCH so a batch containing any geometry sub-op invalidates the dirty
 * pages (which are the union of every sub-op's dirty pages). Nested
 * BATCHes are reducer-rejected (see `ir.ts:Batch`), so the recursion
 * terminates at depth 1.
 */
export function shouldInvalidateBboxCache(op) {
    if (op.kind === "BATCH")
        return op.ops.some(shouldInvalidateBboxCache);
    return GEOMETRY_OP_KINDS.has(op.kind);
}
// ─── Dispatch ─────────────────────────────────────────────────────────────────
/**
 * Apply an op to `state.ir`, rebuild `state.blocksByPage` if a non-doc-level
 * page was touched, then route per-page notifications to subscribers.
 *
 * Returns the `ReducerResult` so callers (notably `applyPowerEditorOp` in
 * editor-card-pane.ts) can branch on `dirtyPages` for their own post-commit
 * work (oplog push, IDB persist, etc.).
 *
 * Errors thrown by the reducer propagate unchanged — the dispatch contract
 * is "commit-on-success, never on partial." If the reducer throws, neither
 * `state.ir` nor `state.blocksByPage` is mutated, and no subscriber fires.
 */
export function dispatch(op) {
    if (!state.ir) {
        throw new Error("dispatcher: state.ir is null — cannot dispatch before initial load");
    }
    // Phase FB.12 perf seam — measure the full dispatch (reducer fold +
    // commit + notify) so test harnesses can assert against the §P.1
    // interactive (16 ms) / non-interactive (100 ms) budgets. The label
    // includes op.kind so per-action profiling is legible in trace tools.
    const dispatchId = _perfBegin(`dispatcher:dispatch:${op.kind}`);
    try {
        const result = apply(state.ir, op);
        commitResult(result, op);
        return result;
    }
    finally {
        _perfEnd(`dispatcher:dispatch:${op.kind}`, dispatchId);
    }
}
/**
 * Commit a pre-computed ReducerResult: assign `state.ir = result.ir`,
 * rebuild `state.blocksByPage` if a non-doc-level page was touched, then
 * route per-page notifications to subscribers.
 *
 * Exposed so `applyPowerEditorOp` (editor-card-pane.ts) can pre-validate
 * an op via `apply(state.ir, op)` (which the dispatcher would have called
 * internally anyway), interleave a WASM round-trip, and only THEN commit.
 * Without this seam the dispatcher would have to host the WASM call or
 * the caller would need to duplicate the commit + notify loop.
 *
 * `op` is optional but SHOULD be passed by callers that know the original
 * action — the Phase FB.11 bbox-cache narrow-invalidation hook reads
 * `op.kind` to decide whether `result.dirtyPages` reflect a geometry
 * change. Without `op`, the cache is left untouched (the safe default —
 * `applyZoom` calls `_bboxCache.clear()` separately, and the `replaceIR`
 * load/teardown paths are handled by their callers via
 * `_bboxCache.clear()` / `resetEditorState`). The dispatcher itself
 * never bypasses this hook; if you add a new caller, pass `op` or
 * document why the cache stays valid.
 */
export function commitResult(result, op) {
    state.ir = result.ir;
    if (_shouldRebuildBlocksByPage(result.dirtyPages)) {
        rebuildBlocksByPage(result.ir);
    }
    // Phase FB.11 — narrow bbox-cache invalidation. Geometry ops evict
    // only the pages in `result.dirtyPages`; metadata ops leave the
    // cache intact. Page 0 (doc-level sentinel) is a safe no-op inside
    // `invalidatePages` — no entry is ever inserted under it.
    if (op !== undefined && shouldInvalidateBboxCache(op)) {
        _bboxCache.invalidatePages(result.dirtyPages);
    }
    _notifySubscribers(result.dirtyPages);
}
/**
 * Internal helper — fire `onChange` on every subscriber whose viewport
 * intersects `dirty` (or who opts into doc-level via `wantsDocLevel`).
 * Extracted so both `commitResult` and `replaceIR` share the same
 * routing rules (no drift class).
 */
function _notifySubscribers(dirty) {
    // Phase FB.12 perf seam — measure just the notify loop. Tests use this
    // to verify the per-subscriber routing cost stays viewport-bounded
    // (§P.3: O(notified-subscribers ≤ viewport-window)).
    const notifyId = _perfBegin("dispatcher:notify");
    try {
        const hasDocLevel = dirty.has(0);
        for (const sub of _subscribers) {
            const visible = _intersectWithViewport(dirty, sub.viewportRange());
            if (visible.size === 0 && !(hasDocLevel && sub.wantsDocLevel))
                continue;
            let payload;
            if (visible.size === 0 && hasDocLevel && sub.wantsDocLevel) {
                payload = new Set([0]);
            }
            else if (hasDocLevel && sub.wantsDocLevel) {
                payload = new Set(visible);
                payload.add(0);
            }
            else {
                payload = visible;
            }
            sub.onChange(state, payload);
        }
    }
    finally {
        _perfEnd("dispatcher:notify", notifyId);
    }
}
/**
 * Replace `state.ir` with a non-reducer-shaped value (initial load,
 * teardown, save-refresh). Routes through the same commit + notify
 * surface as `dispatch()` so subscribers observe the transition. The
 * `dirtyPages` payload signals a full-doc refresh: union of every
 * block-carried page + the doc-level sentinel `0`. Teardown (null IR)
 * fires only doc-level.
 */
export function replaceIR(newIr, _reason) {
    state.ir = newIr;
    state.blocksByPage.clear();
    if (newIr && Array.isArray(newIr.blocks)) {
        rebuildBlocksByPage(newIr);
    }
    // Compute the "all pages touched + doc-level" dirty set so every
    // subscriber observes the transition. Page 0 is the doc-level
    // sentinel; the numeric pages drive viewport-intersecting per-page
    // notifications.
    const dirty = new Set([0]);
    if (newIr && Array.isArray(newIr.blocks)) {
        for (const b of newIr.blocks) {
            if (typeof b.page === "number")
                dirty.add(b.page);
        }
    }
    _notifySubscribers(dirty);
}
